import { useEffect, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { IconName } from '@/components/icon/index.js';
import { Icon } from '@/components/icon/index.js';
import { Button } from '../design-system/primitives/Button/Button.js';
import { cn } from '../../lib/utils.js';
import { useAdminStore } from '../../store/adminStore.js';
import { GatorMark } from '../shared/GatorMark.js';

type AdminSidebarProps = {
  collapsed: boolean;
  mobileOpen: boolean;
  onCloseMobile: () => void;
  onToggleCollapsed: () => void;
};

type NavItem = {
  to: string;
  labelKey: string;
  defaultLabel: string;
  iconName: IconName;
};

type NavGroup = {
  label: string;
  items: NavItem[];
};

const groups: NavGroup[] = [
  {
    label: 'Overview',
    items: [
      { to: '/admin', labelKey: 'admin.sidebar.overview', defaultLabel: 'Overview', iconName: 'home' },
      { to: '/admin/analytics', labelKey: 'admin.sidebar.analytics', defaultLabel: 'Analytics', iconName: 'bar-chart-3' },
    ],
  },
  {
    label: 'Users & Sessions',
    items: [
      { to: '/admin/users', labelKey: 'admin.sidebar.users', defaultLabel: 'Users', iconName: 'users' },
      { to: '/admin/simulations', labelKey: 'admin.sidebar.simulations', defaultLabel: 'Simulations', iconName: 'file-text' },
      { to: '/admin/payments', labelKey: 'admin.sidebar.payments', defaultLabel: 'Payments', iconName: 'credit-card' },
    ],
  },
  {
    label: 'Content',
    items: [
      { to: '/admin/prompts', labelKey: 'admin.sidebar.prompts', defaultLabel: 'Prompts', iconName: 'code-2' },
      {
        to: '/admin/interview-settings',
        labelKey: 'admin.sidebar.interviewSettings',
        defaultLabel: 'Interview Defaults',
        iconName: 'settings-2',
      },
      { to: '/admin/promo-codes', labelKey: 'admin.sidebar.promoCodes', defaultLabel: 'Promo Codes', iconName: 'gift' },
    ],
  },
  {
    label: 'Billing',
    items: [
      { to: '/admin/plans', labelKey: 'admin.sidebar.planDefinitions', defaultLabel: 'Plans', iconName: 'credit-card' },
      { to: '/admin/promotions', labelKey: 'admin.sidebar.promotions', defaultLabel: 'Promotions', iconName: 'sparkles' },
      { to: '/admin/campaigns', labelKey: 'admin.sidebar.campaigns', defaultLabel: 'Campaigns', iconName: 'star' },
      { to: '/admin/testers', labelKey: 'admin.sidebar.testers', defaultLabel: 'Testers', iconName: 'shield' },
      { to: '/admin/discount-codes', labelKey: 'admin.sidebar.discountCodes', defaultLabel: 'Discount Codes', iconName: 'tag' },
      { to: '/admin/outreach', labelKey: 'admin.sidebar.outreach', defaultLabel: 'Outreach', iconName: 'send' },
    ],
  },
  {
    label: 'Communications',
    items: [
      { to: '/admin/support', labelKey: 'admin.sidebar.support', defaultLabel: 'Support', iconName: 'message-circle' },
      { to: '/admin/newsletters', labelKey: 'admin.sidebar.newsletters', defaultLabel: 'Newsletters', iconName: 'mail' },
      {
        to: '/admin/newsletters/subscribers',
        labelKey: 'admin.sidebar.newsletterSubscribers',
        defaultLabel: 'Subscribers',
        iconName: 'user-round-cog',
      },
      { to: '/admin/email-test', labelKey: 'admin.sidebar.emailTest', defaultLabel: 'Email Test', iconName: 'mail-plus' },
    ],
  },
  {
    label: 'System',
    items: [
      { to: '/admin/maintenance', labelKey: 'admin.sidebar.maintenance', defaultLabel: 'Maintenance', iconName: 'triangle-alert' },
      { to: '/admin/logs', labelKey: 'admin.sidebar.logs', defaultLabel: 'Logs', iconName: 'scroll-text' },
      { to: '/admin/test-lab', labelKey: 'admin.sidebar.testLab', defaultLabel: 'Test Lab', iconName: 'flask-conical' },
    ],
  },
];

function isItemActive(pathname: string, to: string): boolean {
  if (to === '/admin') return pathname === '/admin';
  if (to === '/admin/newsletters') {
    return (
      (pathname.startsWith('/admin/newsletters') && !pathname.startsWith('/admin/newsletters/subscribers')) ||
      pathname === '/admin/newsletter/compose'
    );
  }
  if (to === '/admin/newsletters/subscribers') return pathname.startsWith('/admin/newsletters/subscribers');
  return pathname.startsWith(to);
}

export function AdminSidebar({
  collapsed,
  mobileOpen,
  onCloseMobile,
  onToggleCollapsed,
}: AdminSidebarProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const supportNewCount = useAdminStore((s) => s.supportNewCount);
  const loadSupportNewCount = useAdminStore((s) => s.loadSupportNewCount);

  useEffect(() => {
    void loadSupportNewCount();
  }, [loadSupportNewCount]);

  const containerClass = useMemo(
    () =>
      cn(
        'fixed inset-y-0 left-0 z-40 flex h-screen shrink-0 flex-col border-r border-ig-border bg-ig-bg-secondary transition-transform lg:static lg:h-auto lg:min-h-screen lg:self-stretch lg:translate-x-0',
        collapsed ? 'w-16' : 'w-60',
        mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      ),
    [collapsed, mobileOpen]
  );

  return (
    <>
      {mobileOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-30 bg-ig-bg-overlay lg:hidden"
          aria-label={t('admin.sidebar.closeOverlay', { defaultValue: 'Close admin sidebar overlay' })}
          onClick={onCloseMobile}
        />
      ) : null}
      <aside className={containerClass}>
        <div className="flex items-center justify-between px-3 pb-3 pt-4">
          <div className="flex min-w-0 items-center gap-2">
            <GatorMark className="h-8 w-8 shrink-0 rounded-md" alt="" />
            {!collapsed ? (
              <div className="flex min-w-0 flex-col">
                <span className="truncate font-sans text-[15px] font-semibold text-ig-text-primary">
                  InterviewGator
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-ig-text-tertiary">
                  {t('admin.sidebar.admin', { defaultValue: 'ADMIN' })}
                </span>
              </div>
            ) : null}
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="h-8 w-8 border-ig-border bg-transparent p-0"
            onClick={onToggleCollapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Icon name={collapsed ? 'chevron-right' : 'chevron-left'} className="h-4 w-4" />
          </Button>
        </div>
        <nav className="flex flex-1 flex-col gap-3 px-2 pb-4">
          {groups.map((group) => (
            <div key={group.label} className="space-y-1">
              {!collapsed ? (
                <p className="px-2 text-[10px] uppercase tracking-[0.12em] text-ig-text-tertiary">{group.label}</p>
              ) : null}
              {group.items.map((item) => {
                const active = isItemActive(pathname, item.to);
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/admin'}
                    onClick={onCloseMobile}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'group relative flex h-10 items-center rounded-md px-2 text-sm transition',
                      active
                        ? 'bg-ig-accent-muted text-ig-accent'
                        : 'text-ig-text-secondary hover:bg-ig-bg-tertiary hover:text-ig-text-primary'
                    )}
                  >
                    <span
                      className={cn(
                        'absolute left-0 top-1/2 h-6 w-[3px] -translate-y-1/2 rounded-r',
                        active ? 'bg-ig-accent' : 'bg-transparent'
                      )}
                      aria-hidden
                    />
                    <Icon name={item.iconName} className="h-4 w-4 shrink-0" />
                    {!collapsed ? (
                      <span className="ml-3 truncate">{t(item.labelKey, { defaultValue: item.defaultLabel })}</span>
                    ) : null}
                    {item.to === '/admin/support' && supportNewCount > 0 && !collapsed ? (
                      <span className="ml-auto inline-flex min-w-5 items-center justify-center rounded-sm bg-ig-accent px-1.5 text-[10px] font-semibold text-[var(--ig-accent-text)]">
                        {supportNewCount}
                      </span>
                    ) : null}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
}
